'use client';

import { useEffect } from 'react';
import { CookieValueTypes, OptionsType, useCookiesNext } from 'cookies-next';

const MAX_AGE = 60 * 60 * 24 * 365 * 2; // 2 years
const COOKIE_PATH = '/';
const COOKIE_DOMAIN = '.schumacherhomes.com';
const COOKIE_OPTIONS = {
  path: COOKIE_PATH,
  maxAge: MAX_AGE,
  domain: COOKIE_DOMAIN,
};
//hasCookie: (key: string, options?: OptionsType) => boolean
function safeGetCookie(
  cookieName: string,
  getCookie: (key: string, options?: OptionsType) => CookieValueTypes,
) {
  try {
    const existing = getCookie(cookieName);
    if (existing) {
      const parsed = JSON.parse(existing);
      return parsed;
    }
  } catch (e) {
    console.error('Failed to get cookie ', cookieName, e);
  }
}

function tryGetCookie(
  cookieName: string,
  getCookie: (key: string, options?: OptionsType) => CookieValueTypes,
  hasCookie: (key: string, options?: OptionsType) => boolean,
) {
  if (hasCookie(cookieName)) {
    const existing = getCookie(cookieName);

    if (existing) {
      const parsed = tryParseJSON(existing);
      if (parsed) {
        return parsed;
      }

      if (typeof existing === 'string') {
        return existing;
      }

      //we really don't know what this is
      return null;
    }
  }
  return null;
}

function tryParseJSON(jsonString: string) {
  try {
    const parsedObject = JSON.parse(jsonString);
    // Check if the parsed result is actually an object or an array,
    // and not a primitive value like a number or boolean that might technically
    // be valid JSON but not what's expected for a "JSON object."
    if (parsedObject && typeof parsedObject === 'object') {
      return parsedObject;
    }
    // If it's a primitive value, you might choose to return null or throw an error
    // depending on your specific requirements.
    return null;
  } catch (error) {
    // If JSON.parse() throws an error (e.g., SyntaxError for invalid JSON),
    // the catch block will execute.
    console.error('Error parsing JSON:', error);
    return null; // Return null or a default value to indicate failure
  }
}

export const useTrackingCookies = () => {
  const { getCookie, setCookie, deleteCookie, hasCookie } = useCookiesNext();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);

    // GCLID
    const gclid = searchParams.get('gclid');
    //Params should always override the old cookies
    if (gclid) {
      const gclidCookieValue = {
        gclid: searchParams.get('gclid'),
        version: 2,
      };

      //delete if something is found
      if (hasCookie('gclid')) {
        deleteCookie('gclid');
      }
      setCookie('gclid', JSON.stringify(gclidCookieValue), COOKIE_OPTIONS);
    } else {
      //this can either return the string itself or the object
      const parsedGCL = tryGetCookie('gclid', getCookie, hasCookie);
      if (parsedGCL) {
        //if returning object, we do the usual
        if (typeof parsedGCL === 'object') {
          if (parsedGCL && (!parsedGCL.version || parsedGCL.version < 2)) {
            let gclidValue = '';
            if (parsedGCL.gclid) {
              gclidValue = parsedGCL.gclid;
            }
            if (parsedGCL.Gclid) {
              gclidValue = parsedGCL.Gclid;
            }

            //make sure there is an actual value here!
            if (gclidValue && gclidValue.length > 0) {
              const gclidCookieValue = {
                gclid: gclidValue,
                version: 2,
              };
              deleteCookie('gclid');
              setCookie(
                'gclid',
                JSON.stringify(gclidCookieValue),
                COOKIE_OPTIONS,
              );
            }
          }
        } else {
          //else statement if the safeGetCookie2 returns the string
          // just a verification in case of string
          const gclidCookieValue = {
            gclid: parsedGCL,
            version: 2,
          };
          deleteCookie('gclid');
          setCookie('gclid', JSON.stringify(gclidCookieValue), COOKIE_OPTIONS);
        }
      }
    }

    // MSCLKID
    const msclkid = searchParams.get('msclkid');
    //Params should always override the old cookies
    if (msclkid) {
      const msclkidCookieValue = {
        msclkid: searchParams.get('msclkid'),
        version: 2,
      };

      //delete if something is found
      if (hasCookie('msclkid')) {
        deleteCookie('msclkid');
      }
      setCookie('msclkid', JSON.stringify(msclkidCookieValue), COOKIE_OPTIONS);
    } else {
      //this can either return the string itself or the object
      const parsedMSC = tryGetCookie('msclkid', getCookie, hasCookie);
      if (parsedMSC) {
        //if returning object, we do the usual
        if (typeof parsedMSC === 'object') {
          if (parsedMSC && (!parsedMSC.version || parsedMSC.version < 2)) {
            let msclkidValue = '';
            if (parsedMSC.msclkid) {
              msclkidValue = parsedMSC.msclkid;
            }
            if (parsedMSC.Msclkid) {
              msclkidValue = parsedMSC.Msclkid;
            }

            //make sure there is an actual value here!
            if (msclkidValue && msclkidValue.length > 0) {
              const msclkidCookieValue = {
                msclkid: msclkidValue,
                version: 2,
              };
              deleteCookie('msclkid');
              setCookie(
                'msclkid',
                JSON.stringify(msclkidCookieValue),
                COOKIE_OPTIONS,
              );
            }
          }
        } else {
          //else statement if the safeGetCookie2 returns the string
          // just a verification in case of string
          const msclkidCookieValue = {
            msclkid: parsedMSC,
            version: 2,
          };
          deleteCookie('msclkid');
          setCookie(
            'msclkid',
            JSON.stringify(msclkidCookieValue),
            COOKIE_OPTIONS,
          );
        }
      }
    }

    // UTM Parameters
    const utmCampaign = searchParams.get('utm_campaign');
    //Params should always override the old cookies
    if (utmCampaign) {
      const utmCookieValue = {
        campaign: utmCampaign,
        source: searchParams.get('utm_source') ?? '',
        medium: searchParams.get('utm_medium') ?? '',
        term: searchParams.get('utm_term') ?? '',
        content: searchParams.get('utm_content') ?? '',
        version: 2,
      };

      //delete if something is found
      if (hasCookie('utm')) {
        deleteCookie('utm');
      }
      setCookie('utm', JSON.stringify(utmCookieValue), COOKIE_OPTIONS);
    } else {
      //this can either return the string itself or the object
      const parsedUTM = tryGetCookie('utm', getCookie, hasCookie);
      if (parsedUTM) {
        //if returning object, we do the usual
        if (typeof parsedUTM === 'object') {
          if (parsedUTM && (!parsedUTM.version || parsedUTM.version < 2)) {
            let utmCampaign = '';
            let utmSource = '';
            let utmMedium = '';
            let utmTerm = '';
            let utmContent = '';

            if (parsedUTM.campaign) {
              utmCampaign = parsedUTM.campaign;
              utmSource = parsedUTM.source;
              utmMedium = parsedUTM.medium;
              utmTerm = parsedUTM.term;
              utmContent = parsedUTM.content;
            }

            if (parsedUTM.Campaign) {
              utmCampaign = parsedUTM.Campaign;
              utmSource = parsedUTM.Source;
              utmMedium = parsedUTM.Medium;
              utmTerm = parsedUTM.Term;
              utmContent = parsedUTM.Content;
            }

            //make sure there is an actual value here!
            if (utmCampaign && utmCampaign.length > 0) {
              const utmCookieValue = {
                campaign: utmCampaign ?? '',
                source: utmSource ?? '',
                medium: utmMedium ?? '',
                term: utmTerm ?? '',
                content: utmContent ?? '',
                version: 2,
              };
              deleteCookie('utm');
              setCookie('utm', JSON.stringify(utmCookieValue), COOKIE_OPTIONS);
            }
          }
        }
      }
    }

    // epik-Pinterest
    const epik = searchParams.get('epik');
    if (epik) {
      setCookie('epik', epik, COOKIE_OPTIONS);
    }

    // fbclid-Meta/FB
    const fbclid = searchParams.get('fbclid');
    if (fbclid) {
      setCookie('fbclid', fbclid, COOKIE_OPTIONS);
    }

    // tbclid-Taboolah
    const tbclid = searchParams.get('tbclid');
    if (tbclid) {
      setCookie('tbclid', tbclid, COOKIE_OPTIONS);
    }
  }, [deleteCookie, getCookie, setCookie, hasCookie]);
};
